import moment, { Moment } from 'moment';

export interface SettlementsColumnVisibility {
  id: boolean;
  dateTime: boolean;
  client: boolean;
  locations: boolean;
  state: boolean;
  contado: boolean;
  cuenta_corriente: boolean;
  total_precio_espera: boolean;
  total_gastos: boolean;
  total_a_facturar: boolean;
}

export const settlementsColumnLabels: Record<
  keyof SettlementsColumnVisibility,
  string
> = {
  id: 'ID',
  dateTime: 'Fecha',
  client: 'Cliente',
  locations: 'Recorrido',
  state: 'Estado',
  contado: 'Contado',
  cuenta_corriente: 'Cuenta Corriente',
  total_precio_espera: 'Espera',
  total_gastos: 'Gastos',
  total_a_facturar: 'Total'
};

export interface DriverPendingSettlementsFiltersType {
  dateFrom: Moment;
  dateTo: Moment;
}

export interface SettlementsState {
  filters: DriverPendingSettlementsFiltersType;
  columnVisibility: SettlementsColumnVisibility;
  lastAccessDate?: string;
}

const STORAGE_KEY = 'driverSettlementsState';

export const getDefaultFilters = (): DriverPendingSettlementsFiltersType => ({
  dateFrom: moment().subtract(2, 'week'),
  dateTo: moment()
});

export const getDefaultColumnVisibility = (): SettlementsColumnVisibility => ({
  id: true,
  dateTime: true,
  client: true,
  locations: true,
  state: true,
  contado: true,
  cuenta_corriente: true,
  total_precio_espera: true,
  total_gastos: true,
  total_a_facturar: true
});

const getDefaultState = (): SettlementsState => ({
  filters: getDefaultFilters(),
  columnVisibility: getDefaultColumnVisibility()
});

const deserializeState = (
  storedState: Partial<SettlementsState>
): SettlementsState => {
  const defaultFilters = getDefaultFilters();
  const today = moment().format('YYYY-MM-DD');
  const lastAccessDate = storedState.lastAccessDate;
  const isNewDay = !lastAccessDate || lastAccessDate !== today;
  
  return {
    ...getDefaultState(),
    ...storedState,
    filters: {
      ...defaultFilters,
      ...(storedState.filters || {}),
      dateFrom: isNewDay
        ? defaultFilters.dateFrom
        : storedState.filters?.dateFrom
        ? moment(storedState.filters.dateFrom)
        : defaultFilters.dateFrom,
      dateTo: isNewDay
        ? defaultFilters.dateTo
        : storedState.filters?.dateTo
        ? moment(storedState.filters.dateTo)
        : defaultFilters.dateTo
    },
    lastAccessDate: today
  };
};

export const loadStateFromLocalStorage = (): SettlementsState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return deserializeState(parsed);
    }
  } catch (error) {
    console.error('Error loading settlements state:', error);
  }
  return getDefaultState();
};

export const saveStateToLocalStorage = (state: SettlementsState): void => {
  try {
    const stateWithDate = {
      ...state,
      lastAccessDate: moment().format('YYYY-MM-DD')
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithDate));
  } catch (error) {
    console.error('Error saving settlements state:', error);
  }
};

export const clearStateFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing settlements state:', error);
  }
};

